import { kebabCase } from "change-case";
import type { BeforeSendFn } from "posthog-js";

import { isString, routes } from "@/lib/utils";
import { dashboards } from "@/content/analytics.content";

const ANALYTICS_PATHNAME_PATTERN = new RegExp(
  `^${routes.analyticsDashboard("(.+)")}$`
);

/**
 * Reuses the existing dashboards array from analytics.content.ts — no
 * second ID→label mapping to keep in sync.
 */
const dashboardSlugById = new Map(
  dashboards.map(({ id, label }) => [String(id), kebabCase(label)])
);

/**
 * Rewrites `/analytics/<id>` to `/analytics/<slug>` on every outgoing
 * capture, so PostHog shows a readable dashboard name instead of a numeric
 * Metabase ID in $pathname/$current_url. No-op for every other route.
 */
export const rewriteAnalyticsPathname: BeforeSendFn = (captureResult) => {
  if (!captureResult) {
    return captureResult;
  }

  const { $pathname, $current_url } = captureResult.properties;
  const match = isString($pathname)
    ? $pathname.match(ANALYTICS_PATHNAME_PATTERN)
    : null;

  if (!match) {
    return captureResult;
  }

  const [, dashboardId] = match;
  const slug = dashboardSlugById.get(dashboardId);

  if (!slug) {
    return captureResult;
  }

  const rewrittenPathname = routes.analyticsDashboard(slug);
  captureResult.properties.$pathname = rewrittenPathname;

  if (isString($current_url)) {
    try {
      const currentUrl = new URL($current_url);
      currentUrl.pathname = rewrittenPathname;
      captureResult.properties.$current_url = currentUrl.toString();
    } catch {
      /**
       * Leave $current_url unchanged if it isn't a valid absolute URL
       */
    }
  }

  return captureResult;
};
